import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import MangaCard from "@/components/base/MangaCard";
import { mangaList } from "@/mocks/manga";

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = mangaList.find((m) => m.id === id);
  const [descOrder, setDescOrder] = useState(true);
  const [collected, setCollected] = useState(false);

  const related = useMemo(() => {
    if (!manga) return [];
    return mangaList.filter((m) => m.id !== manga.id && (m.category === manga.category || m.tags.some((t) => manga.tags.includes(t)))).slice(0, 5);
  }, [manga]);

  const chapters = useMemo(() => {
    if (!manga) return [];
    return Array.from({ length: Math.min(manga.chapters, 12) }, (_, i) => {
      const num = descOrder ? manga.chapters - i : i + 1;
      return `第${num}话`;
    });
  }, [manga, descOrder]);

  if (!manga) {
    return (
      <>
        <Header />
        <main className="pt-16">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-lg font-medium text-foreground-800">这部漫画没有找到</p>
            <p className="mt-1 text-sm text-foreground-500">它可能已经下架，或链接有误</p>
            <Link to="/list" className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 hover:bg-primary-600">
              返回漫画库
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: manga.title,
    url: `https://stripvault.github.io/manga/${manga.id}`,
    author: { "@type": "Person", name: manga.author },
    description: manga.desc,
    genre: manga.category,
    image: manga.cover,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: manga.rating,
      bestRating: 5,
      ratingCount: Math.max(200, manga.rating * 1000),
    },
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

        {/* Breadcrumb */}
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-4 md:px-6">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to="/list" className="hover:text-primary-600">漫画库</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to={`/list?cat=${encodeURIComponent(manga.category)}`} className="hover:text-primary-600">{manga.category}漫画</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">{manga.title}</span>
            </nav>
          </div>
        </section>

        {/* Hero info */}
        <section className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-8 md:flex-row">
            <div className="mx-auto w-56 shrink-0 sm:w-60">
              <div className="overflow-hidden rounded-2xl ring-1 ring-background-200">
                <img src={manga.cover} alt={`${manga.title}漫画封面`} className="w-full object-cover object-top" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">{manga.category}</span>
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">{manga.artStyle}画风</span>
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">{manga.status}</span>
              </div>
              <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">{manga.title}</h1>
              <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
                <span>作者：{manga.author}</span>
                <span className="flex items-center gap-1">
                  甜度：
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className={`ri-heart-fill text-xs ${manga.sweetness >= i - 0.25 ? "text-accent-500" : "text-background-300"}`} aria-hidden="true"></i>
                  ))}
                </span>
                <span className="flex items-center gap-1 text-secondary-700">
                  <i className="ri-star-fill text-secondary-500" aria-hidden="true"></i>
                  {manga.rating.toFixed(1)}
                </span>
                <span>共 {manga.chapters} 话</span>
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600">{manga.desc}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {manga.tags.map((t) => (
                  <span key={t} className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-600">#{t}</span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/list"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill" aria-hidden="true"></i>
                  开始阅读
                </Link>
                <button
                  onClick={() => setCollected((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-6 py-2.5 text-sm transition-colors ${
                    collected ? "bg-accent-500 text-background-50 hover:bg-accent-600" : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:ring-primary-300"
                  }`}
                >
                  <i className={collected ? "ri-bookmark-fill" : "ri-bookmark-line"} aria-hidden="true"></i>
                  {collected ? "已收藏" : "加入收藏"}
                </button>
                <button className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm text-foreground-700 ring-1 ring-background-300 transition-colors hover:ring-primary-300">
                  <i className="ri-notification-3-line" aria-hidden="true"></i>
                  追更提醒
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters + sidebar */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="rounded-2xl bg-background-50 ring-1 ring-background-200">
                <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
                  <h2 className="font-heading text-lg text-foreground-900">章节列表</h2>
                  <button
                    onClick={() => setDescOrder((v) => !v)}
                    className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 hover:text-primary-600"
                  >
                    <i className={descOrder ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"} aria-hidden="true"></i>
                    {descOrder ? "最新在前" : "最早在前"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2.5 p-5 sm:grid-cols-3">
                  {chapters.map((c) => (
                    <Link
                      key={c}
                      to={`/manga/${manga.id}`}
                      className="rounded-lg bg-background-100/70 px-3 py-2.5 text-center text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      {c}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-background-100 px-5 py-4 text-center">
                  <span className="text-xs text-foreground-400">共 {manga.chapters} 话 · 已展示最近 12 话</span>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <h2 className="mb-4 font-heading text-lg text-foreground-900">相似推荐</h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-2">
                {related.map((m) => (
                  <MangaCard key={m.id} manga={m} />
                ))}
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}